import { pipe } from 'fp-ts/function';
import { array, option, record } from 'fp-ts';
import {
    CheckInConfiguration,
    CheckInConfigurationType,
    CheckInConfigurationUpdate,
    CheckInConfigurationValue,
} from '@mews-commander/data-access';
import { keyBy } from '@mews/fp';
import { CheckInCheckboxStates, CheckInFieldsValues } from '../types';
import { configurationTypeSections } from '../sections';
import { allDocumentFields, documentTypeToFields, driversLicenseFields } from '../documentsTable/documentUtils';
import { getCheckInSameValues } from '../utils';

interface ConvertTableValuesToUpdatesOptions {
    tableValues: CheckInFieldsValues;
    configurations: CheckInConfiguration[];
    checkboxStates: CheckInCheckboxStates;
    areExtendedHotelConfiguredFieldsEnabled: boolean;
    enhancedCheckInFormDocumentsSectionEnabled: boolean;
}

const getValue = (formValue: CheckInConfigurationValue, checked: boolean) =>
    checked ? formValue : CheckInConfigurationValue.DoNotShow;

export const setDefaultDocumentsChildFieldsToDefault = (tableValues: CheckInFieldsValues): CheckInFieldsValues => {
    // special case for PassportOrIdentityCard and DirectDriversLicense
    if (
        tableValues[CheckInConfigurationType.DirectDriversLicense].reservationOwner ===
            CheckInConfigurationValue.DoNotShow &&
        tableValues[CheckInConfigurationType.PassportOrIdentityCardOrDriversLicense].reservationOwner ===
            CheckInConfigurationValue.DoNotShow
    ) {
        // set driver's license fields to do not show
        return driversLicenseFields.reduce<CheckInFieldsValues>(
            (acc, childKey) => {
                acc[childKey] = getCheckInSameValues(CheckInConfigurationValue.DoNotShow);
                return acc;
            },
            { ...tableValues }
        );
    }

    const documentTypeFields = Object.keys(documentTypeToFields);

    const allDefault = documentTypeFields.every(
        (key) => tableValues[key]?.reservationOwner === CheckInConfigurationValue.Default
    );

    if (!allDefault) {
        return tableValues;
    }

    // resets all fields to default for owner, and hidden for adults and children
    return [...documentTypeFields, ...allDocumentFields].reduce<CheckInFieldsValues>(
        (acc, childKey) => {
            const prev = tableValues[childKey]; // we know it exists
            acc[childKey] = {
                ...prev,
                reservationOwner: CheckInConfigurationValue.Default,
                // when all default, there is no checkbox on Documents section how to enable different config for children or adults
                reservationAdult: CheckInConfigurationValue.DoNotShow,
                reservationChild: CheckInConfigurationValue.DoNotShow,
            };
            return acc;
        },
        // start from a shallow copy of everything else
        { ...tableValues }
    );
};

export const convertTableValuesToUpdates = ({
    tableValues,
    configurations,
    areExtendedHotelConfiguredFieldsEnabled,
    enhancedCheckInFormDocumentsSectionEnabled,
    checkboxStates,
}: ConvertTableValuesToUpdatesOptions): CheckInConfigurationUpdate[] => {
    const configByType: Record<string, CheckInConfiguration> = keyBy('type', configurations);
    const updatedTableValues = enhancedCheckInFormDocumentsSectionEnabled
        ? setDefaultDocumentsChildFieldsToDefault(tableValues)
        : tableValues;

    return pipe(
        record.toEntries(updatedTableValues),
        array.filterMap(([key, { reservationAdult, reservationChild, reservationOwner }]) => {
            const config = configByType[key];

            const tabItem = configurationTypeSections[key] ?? null;
            const checkbox = tabItem ? checkboxStates[tabItem] : undefined;

            return option.some({
                id: config.id,
                type: config.type, // Useful for quick reference.
                reservationOwnerValue: { value: reservationOwner },
                reservationAdultValue: areExtendedHotelConfiguredFieldsEnabled
                    ? { value: getValue(reservationAdult, checkbox?.reservationAdult ?? true) }
                    : null,
                reservationChildValue: areExtendedHotelConfiguredFieldsEnabled
                    ? { value: getValue(reservationChild, checkbox?.reservationChild ?? true) }
                    : null,
            });
        })
    );
};
